import React, { useLayoutEffect, useState } from "react";
import { ClientService } from "../../services";
import { TimeLabel } from "../TimeLabel";

import "./styles.css";

// ------------------
// Internal Constants
// ------------------

const defaultColor = "white";
const handRadius = 23;
const arcRadius = 27;
const twoPi = 2 * Math.PI;

// --------------
// Exported Props
// --------------

export interface ITimerProps {
  color?: string;
  pause?: boolean;
  verticalLayout?: boolean;
}

// -------------------
// Exported Components
// -------------------

export const Timer: React.FC<ITimerProps> = ({
  color,
  pause,
  verticalLayout
}) => {
  color = color || defaultColor;

  const [timeLabel, setTimeLabel] = useState(ClientService.default.timeLabel);
  const [timePercentage, setTimePercentage] = useState(
    ClientService.default.timePercentage
  );

  useLayoutEffect(() => {
    if (!pause) {
      ClientService.default.onTimeLabelChanged(setTimeLabel);
      ClientService.default.onTimePercentageChanged(setTimePercentage);
      ClientService.default.onTimeExpired(onTimeExpired);
    } else {
      setTimeLabel(ClientService.default.timeLabel);
      setTimePercentage(ClientService.default.timePercentage);
    }
    return () => {
      ClientService.default.offTimeLabelChanged(setTimeLabel);
      ClientService.default.offTimePercentageChanged(setTimePercentage);
      ClientService.default.offTimeExpired(onTimeExpired);
    };
  }, [pause]);

  function onTimeExpired() {
    ClientService.default.offTimeLabelChanged(setTimeLabel);
    ClientService.default.offTimePercentageChanged(setTimePercentage);
  }

  const percentage = timePercentage || 0;
  const angle = twoPi * percentage;

  return (
    <div
      className="timer"
      tabIndex={-1}
      style={{ flexDirection: verticalLayout ? "column" : "row" }}
    >
      <div className="timer-icon">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 60 60"
          fillRule="evenodd"
          clipRule="evenodd"
          strokeMiterlimit="2"
          pointerEvents="none"
        >
          <path
            d={`M 30,33 L30,8 ${
              percentage < 0.75 ? `A 30,33 0 0 1 ${30 + arcRadius} 33` : ""
            } ${
              percentage < 0.5 ? `A 30,33 0 0 1  30 ${33 + arcRadius}` : ""
            } ${
              percentage < 0.25 ? `A 30,33 0 0 1  ${30 - arcRadius} 33` : ""
            } A 30,33 0 0 1 ${30 + -Math.sin(angle) * arcRadius} ${33 -
              Math.cos(angle) * arcRadius} Z`}
            fill="rgba(0,0,0,0.1)"
          />
          <line
            x1={30}
            y1={33}
            x2={30 - Math.sin(angle) * handRadius}
            y2={33 - Math.cos(angle) * handRadius}
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
          />
          <path
            fill={color}
            d="M56.081,26.022c-0.001-0.003,0-0.007-0.001-0.01c-0.001-0.004-0.004-0.007-0.005-0.011
		c-0.612-2.279-1.518-4.437-2.67-6.437c-0.01-0.021-0.01-0.043-0.022-0.064c-0.012-0.02-0.031-0.031-0.044-0.05
		c-1.011-1.735-2.207-3.347-3.566-4.809l2.147-2.147l1.415,1.414l4.242-4.242l-4.242-4.243l-4.243,4.243l1.414,1.414l-2.147,2.147
		c-1.462-1.358-3.074-2.555-4.809-3.566c-0.019-0.013-0.03-0.032-0.05-0.044c-0.02-0.012-0.044-0.011-0.064-0.022
		c-2-1.153-4.158-2.058-6.436-2.67c-0.004-0.001-0.007-0.004-0.011-0.005c-0.004-0.001-0.007,0-0.01,0
		C35.687,6.574,34.359,6.322,33,6.171V5h1.5C35.879,5,37,3.879,37,2.5S35.879,0,34.5,0h-9C24.121,0,23,1.121,23,2.5S24.121,5,25.5,5
		H27v1.171c-1.359,0.151-2.687,0.403-3.978,0.749c-0.004,0.001-0.007,0-0.01,0.001c-0.004,0.001-0.007,0.004-0.011,0.005
		c-2.278,0.612-4.436,1.518-6.436,2.67c-0.021,0.01-0.044,0.01-0.065,0.022c-0.02,0.012-0.031,0.031-0.05,0.044
		c-1.734,1.011-3.347,2.207-4.809,3.566L9.494,11.08l1.414-1.414L6.665,5.423L2.423,9.666l4.242,4.242l1.415-1.414l2.147,2.147
		c-1.358,1.462-2.555,3.074-3.565,4.809c-0.013,0.019-0.033,0.03-0.044,0.05c-0.012,0.021-0.011,0.043-0.022,0.064
		c-1.153,2-2.058,4.158-2.67,6.437c-0.001,0.004-0.004,0.007-0.005,0.011c-0.001,0.003,0,0.007-0.001,0.01
		C3.323,28.25,3,30.587,3,33s0.323,4.75,0.919,6.978c0.001,0.004,0,0.007,0.001,0.01c0.001,0.005,0.005,0.009,0.006,0.014
		c0.612,2.278,1.517,4.435,2.669,6.434c0.01,0.021,0.01,0.043,0.022,0.064c0.017,0.029,0.041,0.051,0.06,0.078
		c1.165,1.993,2.573,3.827,4.191,5.454c0.016,0.019,0.022,0.042,0.04,0.06s0.041,0.024,0.06,0.04
		c1.634,1.626,3.478,3.039,5.482,4.207c0.019,0.013,0.03,0.032,0.05,0.044c0.03,0.017,0.063,0.025,0.094,0.039
		c1.991,1.144,4.14,2.044,6.407,2.653c0.004,0.001,0.007,0.004,0.011,0.005c0.005,0.001,0.01,0.001,0.015,0.002
		C25.253,59.678,27.589,60,30,60s4.747-0.322,6.973-0.918c0.005-0.001,0.01,0,0.015-0.002c0.004-0.001,0.007-0.004,0.011-0.005
		c2.267-0.609,4.415-1.509,6.407-2.653c0.031-0.014,0.064-0.021,0.094-0.039c0.02-0.012,0.031-0.031,0.05-0.044
		c2.004-1.168,3.847-2.581,5.482-4.207c0.019-0.016,0.042-0.022,0.06-0.04s0.024-0.042,0.04-0.06
		c1.618-1.627,3.026-3.461,4.191-5.454c0.019-0.027,0.043-0.048,0.06-0.078c0.012-0.021,0.011-0.043,0.022-0.064
		c1.152-1.999,2.057-4.156,2.669-6.434c0.001-0.005,0.005-0.009,0.006-0.014c0.001-0.003,0-0.007,0.001-0.01
		C56.677,37.75,57,35.413,57,33S56.677,28.25,56.081,26.022z M53.335,8.251l1.414,1.415l-1.414,1.414L51.92,9.666L53.335,8.251z
		 M5.251,9.666l1.414-1.415L8.08,9.666L6.665,11.08L5.251,9.666z M51.957,39.919l1.915,0.513c-0.455,1.458-1.045,2.857-1.747,4.188
		l-1.707-0.986c-0.478-0.277-1.09-0.114-1.366,0.366c-0.276,0.479-0.112,1.09,0.366,1.366l1.703,0.983
		c-0.813,1.282-1.736,2.486-2.762,3.596l-1.389-1.389c-0.391-0.391-1.023-0.391-1.414,0s-0.391,1.023,0,1.414l1.389,1.389
		c-1.11,1.026-2.314,1.949-3.596,2.762l-0.983-1.703c-0.277-0.48-0.89-0.644-1.366-0.366c-0.479,0.276-0.643,0.888-0.366,1.366
		l0.986,1.707c-1.331,0.702-2.729,1.292-4.188,1.747l-0.513-1.914c-0.143-0.534-0.691-0.852-1.225-0.707
		c-0.533,0.143-0.85,0.691-0.707,1.225l0.511,1.908c-1.456,0.328-2.959,0.53-4.498,0.591V56c0-0.553-0.447-1-1-1s-1,0.447-1,1v1.975
		c-1.539-0.061-3.042-0.263-4.498-0.591l0.511-1.908c0.143-0.533-0.174-1.082-0.707-1.225c-0.533-0.144-1.082,0.173-1.225,0.707
		l-0.513,1.914c-1.458-0.455-2.857-1.045-4.188-1.747l0.986-1.707c0.276-0.479,0.112-1.09-0.366-1.366
		c-0.478-0.277-1.09-0.114-1.366,0.366l-0.983,1.703c-1.282-0.813-2.486-1.736-3.596-2.762l1.389-1.389
		c0.391-0.391,0.391-1.023,0-1.414s-1.023-0.391-1.414,0l-1.389,1.389c-1.026-1.11-1.949-2.314-2.762-3.596l1.703-0.983
		c0.479-0.276,0.643-0.888,0.366-1.366c-0.277-0.48-0.89-0.644-1.366-0.366l-1.707,0.986c-0.702-1.331-1.292-2.729-1.747-4.188
		l1.915-0.513c0.533-0.143,0.85-0.691,0.707-1.225c-0.142-0.534-0.69-0.852-1.225-0.707l-1.909,0.511
		C5.289,37.042,5.086,35.539,5.025,34H7c0.553,0,1-0.447,1-1s-0.447-1-1-1H5.025c0.061-1.539,0.263-3.042,0.591-4.498l1.909,0.512
		c0.087,0.023,0.174,0.034,0.26,0.034c0.441,0,0.845-0.294,0.965-0.741c0.144-0.533-0.174-1.082-0.707-1.225l-1.915-0.514
		c0.455-1.458,1.045-2.857,1.747-4.188l1.707,0.986C9.738,22.457,9.91,22.5,10.08,22.5c0.346,0,0.682-0.179,0.867-0.5
		c0.276-0.479,0.112-1.09-0.366-1.366l-1.703-0.983c0.813-1.282,1.736-2.486,2.762-3.596l1.389,1.389
		c0.195,0.195,0.451,0.293,0.707,0.293s0.512-0.098,0.707-0.293c0.391-0.391,0.391-1.023,0-1.414l-1.389-1.389
		c1.11-1.025,2.314-1.949,3.596-2.762l0.983,1.704c0.186,0.32,0.521,0.5,0.867,0.5c0.17,0,0.342-0.043,0.499-0.134
		c0.479-0.276,0.643-0.888,0.366-1.366l-0.986-1.708c1.331-0.702,2.729-1.292,4.188-1.747l0.513,1.915
		c0.119,0.446,0.523,0.741,0.965,0.741c0.086,0,0.173-0.011,0.26-0.034c0.533-0.143,0.85-0.691,0.707-1.225l-0.511-1.909
		c1.172-0.264,2.373-0.445,3.6-0.537c0.31-0.023,0.59-0.04,0.861-0.053c0.013,0,0.025,0,0.038-0.001V10c0,0.553,0.447,1,1,1
		s1-0.447,1-1V8.025c0.013,0,0.025,0,0.038,0.001c0.271,0.013,0.55,0.03,0.861,0.053c1.227,0.093,2.429,0.273,3.6,0.537
		l-0.511,1.909c-0.143,0.533,0.174,1.082,0.707,1.225c0.087,0.023,0.174,0.034,0.26,0.034c0.441,0,0.846-0.295,0.965-0.741
		l0.513-1.915c1.458,0.455,2.857,1.045,4.188,1.747l-0.986,1.708c-0.276,0.479-0.112,1.09,0.366,1.366
		c0.157,0.091,0.329,0.134,0.499,0.134c0.346,0,0.682-0.18,0.867-0.5l0.983-1.704c1.282,0.813,2.486,1.737,3.596,2.762l-1.389,1.389
		c-0.391,0.391-0.391,1.023,0,1.414c0.195,0.195,0.451,0.293,0.707,0.293s0.512-0.098,0.707-0.293l1.389-1.389
		c1.026,1.11,1.949,2.314,2.762,3.596l-1.703,0.983C48.94,20.91,48.776,21.521,49.053,22c0.186,0.321,0.521,0.5,0.867,0.5
		c0.17,0,0.342-0.043,0.499-0.134l1.707-0.986c0.702,1.331,1.292,2.729,1.747,4.188l-1.915,0.514
		c-0.533,0.143-0.851,0.691-0.707,1.225c0.12,0.447,0.523,0.741,0.965,0.741c0.086,0,0.173-0.011,0.26-0.034l1.909-0.512
		c0.328,1.456,0.53,2.959,0.591,4.498H53c-0.553,0-1,0.447-1,1s0.447,1,1,1h1.975c-0.061,1.539-0.263,3.042-0.591,4.499
		l-1.909-0.511c-0.531-0.144-1.082,0.174-1.225,0.707S51.424,39.776,51.957,39.919z M29,6.022V3h-3.5C25.225,3,25,2.775,25,2.5
		S25.225,2,25.5,2h9C34.775,2,35,2.225,35,2.5S34.775,3,34.5,3H31v3.022C30.65,6.007,30.321,6,30,6S29.35,6.007,29,6.022z"
          />
        </svg>
      </div>
      <div className="timer-remaining-label">
        <TimeLabel label={timeLabel === undefined ? "0:00" : timeLabel} large />
      </div>
    </div>
  );
};
